import {useEffect, useRef, useState, type ReactNode} from 'react';
import {recordPageVisit, startTelemetry, setTelemetryUser} from './telemetry';
import {SessionProvider, useSession} from './account/session';
import {AuthDialog,type WebsiteLoginReceipt} from './AuthDialog';
import {DesktopAuthorizationPage} from './DesktopAuthorizationPage';
import {AccountPage, RedeemPage} from './AccountPage';
import {LegalPage} from './LegalPage';
import {ProductGuide} from './ProductGuide';
import {routes} from './content';
import {downloadBrand, downloadMetadata, officialBrand} from './desktop-brands';
import {LegacyFeatures} from './website/LegacyFeatures';
import {WebsiteHeader, WebsiteFooter, WebsiteContact, HomePage, PageIntro, DownloadPage, EnterprisePage, Closing} from './website/Website';

export default function App({path = '/'}: {path?: string}) {
  return <SessionProvider><Site path={path} /></SessionProvider>;
}

function Site({path}: {path: string}) {
  const routeBrand = downloadBrand(path);
  const brand = routeBrand ?? officialBrand;
  const session = useSession();
  const [authOpen, setAuthOpen] = useState(false);
  const [loginReceipt,setLoginReceipt]=useState<WebsiteLoginReceipt|null>(null);
  const authTrigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (session.status === 'checking' || session.status === 'unavailable') return;
    setTelemetryUser(session.user?.id ?? null);
    recordPageVisit(path, path);
    return startTelemetry();
  }, [path, session.status, session.user?.id]);
  useEffect(() => {document.title = routes[path]?.title || (routeBrand ? downloadMetadata(routeBrand).title : 'Sciland™');}, [path, routeBrand]);
  useEffect(() => {if (path === '/login') setAuthOpen(true);}, [path]);

  let page: ReactNode;
  switch (path) {
    case '/': page = <HomePage />; break;
    case '/features': page = <><PageIntro title={<>与你共同开展<br />专业科研工作。</>} lead="在同一课题中支持文献调研、方案设计、计算分析与科研写作，通过独立审查提出修正与验证要求。" /><LegacyFeatures /><Closing /></>; break;
    case '/download': page = <DownloadPage brand={brand} />; break;
    case '/guides': page = <div className="legacy-guide"><ProductGuide /></div>; break;
    case '/enterprise': page = <EnterprisePage />; break;
    case '/login': page = <PageIntro title={<>登录 / 注册，<br />继续你的研究。</>} lead="使用手机号验证码、密码或微信登录 Sciland™。新手机号通过验证码登录时自动注册。" />; break;
    case '/desktop/authorize': page = <DesktopAuthorizationPage onLogin={()=>setAuthOpen(true)} loginReceipt={loginReceipt}/>; break;
    case '/account': page = <AccountPage />; break;
    case '/redeem': page = <RedeemPage />; break;
    case '/terms': page = <LegalPage kind="terms" />; break;
    case '/privacy': page = <LegalPage kind="privacy" />; break;
    default: page = routeBrand ? <DownloadPage brand={routeBrand} /> : <PageIntro title="这个页面没有找到" lead="返回首页，或从导航中继续浏览。"><a className="r-button r-primary" href="/">返回首页</a></PageIntro>;
  }
  const accountPage = path === '/account' || path === '/redeem';
  return <div className={`site-shell refresh-site r-site r-theme-night r-page-${path === '/' ? 'home' : routeBrand ? 'download' : path.slice(1)}${accountPage ? ' account-shell' : ''}${brand.id !== 'official' ? ' partner-download' : ''}`} onClick={event => {
    const anchor = (event.target as Element).closest?.('a');
    if (anchor?.getAttribute('href') === '/login' && !anchor.classList.contains('auth-live') && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      event.preventDefault(); authTrigger.current = anchor; setAuthOpen(true);
    }
  }}>
    <WebsiteHeader path={path} brand={brand} />
    <main id="main" tabIndex={-1} className={accountPage ? undefined : 'refresh-surface'}>{page}</main>
    <WebsiteFooter home={path === '/'} brand={brand} />
    <WebsiteContact brand={brand} />
    <AuthDialog open={authOpen} onAuthenticated={setLoginReceipt} onClose={() => {
      setAuthOpen(false);
      requestAnimationFrame(() => {
        const trigger = authTrigger.current;
        if (trigger?.isConnected && !trigger.closest('.r-mobile-menu')) trigger.focus();
        else Array.from(document.querySelectorAll<HTMLElement>('.r-site-header .login-link, .r-site-header .r-menu-toggle'))
          .find(element => element.getClientRects().length > 0)?.focus();
      });
    }} />
  </div>;
}
